/**
 * Sprint 26.0 — the Body section (the hero document).
 *
 * The body IS the task — this is the document-centric (body-first) lead of the
 * detail. Display reads the body as prose (markdown-aware: newlines preserved);
 * "Edit" swaps in a large textarea, saved on blur. Agent-facing directive
 * guidance lives separately in the Instructions section.
 */

import { useState, type CSSProperties } from "react";

export interface BodySectionProps {
  value: string;
  onSave: (text: string) => void;
}

export function BodySection({ value, onSave }: BodySectionProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  function commit() {
    setEditing(false);
    if (draft !== value) onSave(draft);
  }

  return (
    <section style={{ marginBottom: 22 }} data-testid="body-section">
      <div style={headerRow}>
        <span style={label}>Document</span>
        {!editing && (
          <button type="button" style={editBtn} onClick={() => { setDraft(value); setEditing(true); }}>
            <i className="ti ti-edit" style={{ fontSize: 12 }} aria-hidden="true" /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          style={textarea}
          data-testid="body-textarea"
        />
      ) : (
        <div style={card}>
          {value.trim() ? (
            <div style={text}>{value}</div>
          ) : (
            <div style={empty}>Click "Edit" to write the task document.</div>
          )}
        </div>
      )}
    </section>
  );
}

const headerRow: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 6,
};

const label: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
};

const editBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
};

const card: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: "14px 16px",
  minHeight: 80,
};

const text: CSSProperties = {
  fontSize: 14,
  lineHeight: 1.7,
  color: "var(--color-text-primary)",
  whiteSpace: "pre-wrap",
};

const empty: CSSProperties = {
  fontSize: 13,
  fontStyle: "italic",
  color: "var(--color-text-tertiary)",
};

const textarea: CSSProperties = {
  width: "100%",
  minHeight: 220,
  background: "var(--color-background-secondary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  padding: "14px 16px",
  fontSize: 14,
  lineHeight: 1.7,
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  resize: "vertical",
  outline: "none",
  boxSizing: "border-box",
};
